import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { getAuth } from 'firebase/auth';

// Função para obter o caminho base da aplicação
const getBasePath = () => {
  const origin = window.location.origin;
  console.log('Base URL:', origin);
  return origin;
};

const SOUND_FILES = {
  // Sons básicos
  'pop': '/sounds/pop.mp3',
  'click': '/sounds/click.mp3',
  'bell': '/sounds/bell.mp3',
  'ding': '/sounds/ding.mp3',
  'chime': '/sounds/chime.mp3',
  'coin': '/sounds/coin.mp3',
  'swoosh': '/sounds/swoosh.mp3',
  'blip': '/sounds/blip.mp3',
  'tap': '/sounds/tap.mp3',
  
  // Sons de conquista
  'achievement': '/sounds/achievement.mp3',
  'fanfare': '/sounds/fanfare.mp3',
  'success': '/sounds/success.mp3',
  'victory': '/sounds/victory.mp3',
  'level_up': '/sounds/level_up.mp3',
  'tada': '/sounds/tada.mp3',
  'applause': '/sounds/applause.mp3',
  'cheer': '/sounds/cheer.mp3',
  'magic': '/sounds/magic.mp3',
  
  // Aliases para compatibilidade (mantendo apenas os necessários)
  'ACHIEVEMENT': '/sounds/achievement.mp3',
  'FANFARE': '/sounds/fanfare.mp3',
  'SUCCESS': '/sounds/success.mp3',
  'VICTORY': '/sounds/victory.mp3',
  'POP': '/sounds/pop.mp3',
  'CONQUISTA': '/sounds/achievement.mp3',
  'VITORIA': '/sounds/victory.mp3',
  'SINO': '/sounds/bell.mp3',
  'MOEDA': '/sounds/coin.mp3',
  'APLAUSO': '/sounds/applause.mp3',
  'MAGICO': '/sounds/magic.mp3',
  'MAGIC': '/sounds/magic.mp3'
};

class SoundService {
  constructor() {
    this.soundSettings = null;
    this.audioCache = new Map();
    this.basePath = getBasePath();
    this.auth = getAuth();
    
    console.log('SoundService initialized');
    console.log('Base path:', this.basePath);
    console.log('Available sounds:', Object.keys(SOUND_FILES));
    
    // Testa o sistema de som
    this.testSound();
  }

  async testSound() {
    try {
      const testAudio = new Audio(this.basePath + '/sounds/pop.mp3');
      console.log('Test audio created:', {
        src: testAudio.src,
        readyState: testAudio.readyState
      });

      testAudio.volume = 0.1; // Volume baixo para o teste
      
      // Adiciona listeners para debug
      testAudio.addEventListener('canplaythrough', () => {
        console.log('Test sound can play through');
      });
      
      testAudio.addEventListener('error', (e) => {
        console.error('Test sound error:', e);
      });
      
      // Carrega o áudio mas não toca
      testAudio.load();
      
    } catch (error) {
      console.error('Error testing sound:', error);
    }
  }

  async loadSoundSettings() {
    try {
      // Usa o usuário atual do Firebase Auth
      const currentUser = this.auth.currentUser;
      console.log('Current Firebase user:', currentUser?.uid);
      
      if (!currentUser) {
        console.log('No authenticated user found');
        return this.getDefaultSettings();
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('Firebase user data:', userData);
        
        // Usa preferences.sounds
        if (!userData.preferences?.sounds) {
          console.log('No sound settings found, creating default settings');
          const defaultSettings = this.getDefaultSettings();
          await this.saveSoundSettings(defaultSettings);
          return defaultSettings;
        }
        
        console.log('Raw sound settings from Firebase:', userData.preferences.sounds);
        
        const normalizedSettings = {
          habitComplete: this.normalizeSoundId(userData.preferences.sounds.habitComplete),
          perfectDay: this.normalizeSoundId(userData.preferences.sounds.perfectDay),
          goalComplete: this.normalizeSoundId(userData.preferences.sounds.goalComplete),
          challengeComplete: this.normalizeSoundId(userData.preferences.sounds.challengeComplete),
          taskComplete: this.normalizeSoundId(userData.preferences.sounds.taskComplete),
          challengeDayComplete: this.normalizeSoundId(userData.preferences.sounds.challengeDayComplete)
        };
        
        console.log('Normalized settings before defaults:', normalizedSettings);
        
        // Verifica se alguma configuração está faltando e atualiza com os valores padrão
        let needsUpdate = false;
        const updatedSettings = { ...normalizedSettings };
        
        if (!normalizedSettings.habitComplete) { updatedSettings.habitComplete = 'POP'; needsUpdate = true; }
        if (!normalizedSettings.perfectDay) { updatedSettings.perfectDay = 'ACHIEVEMENT'; needsUpdate = true; }
        if (!normalizedSettings.goalComplete) { updatedSettings.goalComplete = 'VICTORY'; needsUpdate = true; }
        if (!normalizedSettings.challengeComplete) { updatedSettings.challengeComplete = 'FANFARE'; needsUpdate = true; }
        if (!normalizedSettings.taskComplete) { updatedSettings.taskComplete = 'CLICK'; needsUpdate = true; }
        if (!normalizedSettings.challengeDayComplete) { updatedSettings.challengeDayComplete = 'DING'; needsUpdate = true; }
        
        // Se alguma configuração estava faltando, salva as atualizações
        if (needsUpdate) {
          console.log('Updating missing sound settings with defaults');
          await this.saveSoundSettings(updatedSettings);
          this.soundSettings = updatedSettings;
          return updatedSettings;
        }
        
        this.soundSettings = normalizedSettings;
        return normalizedSettings;
      }
      
      // Se o documento não existe, cria com as configurações padrão
      console.log('User document does not exist, creating with default settings');
      const defaultSettings = this.getDefaultSettings();
      await this.saveSoundSettings(defaultSettings);
      return defaultSettings;
      
    } catch (error) {
      console.error('Erro ao carregar configurações de som:', error);
      return this.getDefaultSettings();
    }
  }

  async saveSoundSettings(settings) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        console.log('No authenticated user found, cannot save settings');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        'preferences.sounds': settings
      });
      console.log('Sound settings saved successfully:', settings);
    } catch (error) {
      console.error('Error saving sound settings:', error);
    }
  }

  getDefaultSettings() {
    const defaultSettings = {
      habitComplete: 'POP',
      perfectDay: 'SUCCESS',
      goalComplete: 'VICTORY',
      challengeComplete: 'FANFARE',
      taskComplete: 'CLICK',
      challengeDayComplete: 'DING'
    };
    
    this.soundSettings = defaultSettings;
    console.log('Using default settings:', defaultSettings);
    return defaultSettings;
  }

  normalizeSoundId(soundId) {
    if (!soundId || soundId === 'SEM_SOM') {
      console.log(`Sound ID "${soundId}" is null, undefined, or SEM_SOM`);
      return null;
    }
    
    console.log(`Normalizing sound ID: "${soundId}"`);
    
    // Mapeamento de nomes antigos
    const nameMapping = {
      'CONQUISTA': 'ACHIEVEMENT',
      'VITORIA': 'VICTORY',
      'SINO': 'BELL',
      'MOEDA': 'COIN',
      'APLAUSO': 'APPLAUSE',
      'MAGICO': 'MAGIC',
      'magic': 'MAGIC'
    };
    
    // Primeiro tenta o nome exato
    if (SOUND_FILES[soundId]) {
      console.log(`Found exact match for: "${soundId}"`);
      return soundId;
    }
    
    // Tenta o nome em maiúsculas
    const upperSoundId = soundId.toUpperCase();
    if (SOUND_FILES[upperSoundId]) {
      console.log(`Found uppercase match for: "${upperSoundId}"`);
      return upperSoundId;
    }
    
    // Tenta o nome em minúsculas
    const lowerSoundId = soundId.toLowerCase();
    if (SOUND_FILES[lowerSoundId]) {
      console.log(`Found lowercase match for: "${lowerSoundId}"`);
      return lowerSoundId;
    }
    
    // Verifica se é um nome antigo
    if (nameMapping[upperSoundId]) {
      const mappedName = nameMapping[upperSoundId];
      console.log(`Mapped old name "${upperSoundId}" to "${mappedName}"`);
      return mappedName;
    }
    
    console.log(`No match found for sound ID: "${soundId}"`);
    return null;
  }

  async playSound(soundId) {
    if (!soundId || soundId === 'SEM_SOM') {
      console.log('Sound disabled or invalid ID');
      return;
    }
    
    const soundPath = SOUND_FILES[soundId];
    if (!soundPath) {
      console.error('Sound not found:', soundId);
      return;
    }

    try {
      const fullPath = this.basePath + soundPath;
      console.log('Playing sound:', fullPath);
      
      let audio = this.audioCache.get(soundId);
      
      if (!audio) {
        audio = new Audio(fullPath);
        this.audioCache.set(soundId, audio);
      }
      
      // Reseta o áudio para garantir que ele toque
      audio.currentTime = 0;
      audio.volume = 1.0;
      
      return new Promise((resolve, reject) => {
        const playPromise = audio.play();
        
        if (playPromise) {
          playPromise
            .then(() => {
              console.log('Sound played successfully:', soundId);
              resolve();
            })
            .catch(error => {
              console.error('Error playing sound:', error);
              // Remove do cache se houver erro
              this.audioCache.delete(soundId);
              reject(error);
            });
        }
      });

    } catch (error) {
      console.error('Error creating audio:', error);
      throw error;
    }
  }

  async playHabitComplete() {
    console.log('=== Playing Habit Complete Sound ===');
    const settings = await this.loadSoundSettings();
    console.log('Settings for habit complete:', settings?.habitComplete);
    if (settings?.habitComplete) {
      await this.playSound(settings.habitComplete);
    }
  }

  async playPerfectDay() {
    console.log('=== Playing Perfect Day Sound ===');
    const settings = await this.loadSoundSettings();
    console.log('Settings for perfect day:', settings?.perfectDay);
    if (settings?.perfectDay) {
      await this.playSound(settings.perfectDay);
    }
  }

  async playGoalComplete() {
    console.log('=== Playing Goal Complete Sound ===');
    const settings = await this.loadSoundSettings();
    console.log('Settings for goal complete:', settings?.goalComplete);
    if (settings?.goalComplete) {
      await this.playSound(settings.goalComplete);
    }
  }

  async playChallengeComplete() {
    console.log('=== Playing Challenge Complete Sound ===');
    const settings = await this.loadSoundSettings();
    console.log('Settings for challenge complete:', settings?.challengeComplete);
    if (settings?.challengeComplete) {
      await this.playSound(settings.challengeComplete);
    }
  }

  async playTaskComplete() {
    if (!this.soundSettings) {
      await this.loadSoundSettings();
    }
    return this.playSound(this.soundSettings.taskComplete);
  }

  async playChallengeDayComplete() {
    console.log('=== Playing Challenge Day Complete Sound ===');
    console.log('Current sound settings:', this.soundSettings);
    const settings = await this.loadSoundSettings();
    console.log('Loaded sound settings:', settings);
    console.log('Settings for challenge day complete:', settings?.challengeDayComplete);
    if (settings?.challengeDayComplete) {
      console.log('Playing sound:', settings.challengeDayComplete);
      await this.playSound(settings.challengeDayComplete);
    } else {
      console.log('No sound setting found for challenge day complete');
    }
  }
}

export const soundService = new SoundService(); 